import {AxiosError} from "axios";
import {TFunction} from "i18next";
import {NavigateFunction} from "react-router/dist/lib/hooks";
import {logger} from "./Logger";
import {StringUtils} from "./StringUtils";

/**
 * Utility function to block the main thread for some time.
 *
 * NOTE: THE MAIN PURPOSE FOR THIS IS TO ARTIFICIALLY INTRODUCE SOME DELAY FOR TESTING PURPOSES DURING DEVELOPMENT
 */
function blockMainThreadWhileBurningYourCPU(ms : number)
{
    logger.warn(`BURNING CPU FOR ${ms} MILLISECONDS`);

    const start = performance.now();

    while (performance.now() - start < ms);
}

function redirectToLoginIfNotAuthenticated (err : Error, navigate : NavigateFunction) {
    if (err instanceof AxiosError && err.response?.status === 401)
    {
        if (err.response?.headers["www-authenticate"] === "TOTP") {
            navigate("/tfa");
        }
        else {
            navigate("/login");
        }
    }
}

function getErrorMessage(err : Error, t : TFunction<string, undefined> | undefined) : string
{
    let errorMessage : string | undefined = undefined;

    if (err instanceof AxiosError)
    {
        let data = err.response?.data;

        if (data !== undefined && data !== null)
        {
            errorMessage = data["message"]

            // error message can contain a nested json message when the error is created by the portal back-end
            if (errorMessage !== undefined && errorMessage !== null)
            {
                try {
                    const nestedError = JSON.parse(errorMessage);

                    if (nestedError["className"] === "com.ciphermail.core.app.properties.PropertyValidationFailedException") {
                        data = nestedError;
                    }
                }
                catch (e) {
                    // ignore
                }
            }

            if (data["className"] === "com.ciphermail.core.app.properties.PropertyValidationFailedException")
            {
                if (t !== undefined) {
                    errorMessage = t(data["key"]);
                }
            }
        }
    }

    if (StringUtils.isBlank(errorMessage) || errorMessage === undefined || errorMessage === null) {
        errorMessage = err.message;
    }

    if (StringUtils.isBlank(errorMessage)) {
        errorMessage = "Unknown error"
    }

    return errorMessage;
}

/**
 * Converts a number-like value to an integer string representation.
 *
 * @param {string | undefined | null} value - The value to convert.
 *
 * @return {string | undefined | null} - The integer string representation of the value.
 */
function numberToIntString(value : string)
{
    return value !== undefined && value !== null && value.length > 0 ? Math.floor(Number(value)).toString() : value;
}

/**
 * Converts a string value to the corresponding enum value.
 *
 * @param {object} enumObject - The enum object.
 * @param {string} value - The string value to convert.
 * @return {string} - The converted enum value.
 * @throws {Error} - If the provided value is not found in the enum.
 */
function stringToEnum<T extends {[key: string]: string}, K extends keyof T & string>(enumObject: T, value: `${T[K]}`): T[K]
{
    if (Object.values(enumObject).includes(value)) {
        return (value as unknown) as T[K];
    } else {
        throw new Error('Value provided was not found in Enum');
    }
}

/**
 * Retrieves the keys of an enum object.
 *
 * @param {Object} enumObject - The enum object.
 * @return {string[]} - An array containing the keys of the enum object.
 */
function getEnumKeys<T extends {[key: string]: string}>(enumObject: T): string[] {
    return Object.keys(enumObject);
}

function formatBytes(bytes : number | undefined | null, decimals = 2) : string
{
    if (bytes === undefined || bytes === null) {
        return "0";
    }

    const k = 1024
    const dm = decimals < 0 ? 0 : decimals
    const sizes = ['Bytes', 'KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB']

    const i = Math.floor(Math.log(bytes) / Math.log(k))

    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`
}

function createPublicHostResourceUrl(id : string){
    return "/api/hostresources/getPublicResource?id=" + id + "&hostname=" + window.location.hostname;
}

function clickOnEnterKeyPressed(event : React.KeyboardEvent<HTMLElement>)
{
    if (event.key === 'Enter') {
        event.preventDefault();
        (event.target as HTMLElement).click()
    }
}

export const Utils = () => {
    return
}

Utils.blockMainThreadWhileBurningYourCPU = blockMainThreadWhileBurningYourCPU;
Utils.redirectToLoginIfNotAuthenticated = redirectToLoginIfNotAuthenticated;
Utils.getErrorMessage = getErrorMessage;
Utils.numberToIntString = numberToIntString;
Utils.stringToEnum = stringToEnum;
Utils.getEnumKeys = getEnumKeys;
Utils.formatBytes = formatBytes;
Utils.createPublicHostResourceUrl = createPublicHostResourceUrl;
Utils.clickOnEnterKeyPressed = clickOnEnterKeyPressed;